import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ASSIGNABLE_ROLES, type Permission, ROLE_PERMISSIONS, UserRole } from '@insio/shared-types';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/** Display order of the roles the "anybody who may approve" sentence names. */
const HINT_ROLE_ORDER: readonly UserRole[] = [
  UserRole.APPROVER,
  UserRole.ACCOUNTANT,
  UserRole.ADMIN
];

/** A role's place in the hint; one the order does not name goes last. */
const hintOrderOf = (role: UserRole): number => {
  const index = HINT_ROLE_ORDER.indexOf(role);
  return index === -1 ? HINT_ROLE_ORDER.length : index;
};

/** The assignable roles whose holders hold the permission, in hint order. */
export const rolesNamedInHint = (permission: Permission): UserRole[] =>
  Object.values(UserRole)
    .filter(role => ROLE_PERMISSIONS[role].includes(permission))
    .filter(role => ASSIGNABLE_ROLES.includes(role))
    .sort((a, b) => hintOrderOf(a) - hintOrderOf(b));

/** "Rozhodnout může Schvalovatel, Účetní nebo Administrátor." for one approve permission. */
export function useAnyApproverHint(permission: Permission): string {
  const t = useFeatureTranslation('procurement');
  const tRole = useEnumTranslation('userRole');
  const { i18n } = useTranslation();
  return useMemo(
    () =>
      t('approval.anyApproverHint', {
        roles: new Intl.ListFormat(i18n.language, { type: 'disjunction' }).format(
          rolesNamedInHint(permission).map(role => tRole(role))
        )
      }),
    [i18n.language, permission, t, tRole]
  );
}
